import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { JSONApiDataObject, NoteV3Model, ReduxEntity } from 'Douleutaras/types';
import { RootState } from 'Douleutaras/redux/store';
import { makeRequest } from 'Douleutaras/services/apiClient';

// V3 JSON:API `note` resource. Replaces the legacy V2 `api/public/note` flow.
const NOTE_ENDPOINT = 'api/public/v3/note';
const NOTE_RESOURCE_TYPE = 'note';
const JOB_RESOURCE_TYPE = 'jobs';
const PAGE_SIZE = '100';

type NoteObject = JSONApiDataObject<NoteV3Model>;

interface RejectValue {
  rejectValue: { errors?: Array<string>, serverError?: string, error?: string };
}

const noteAdapter = createEntityAdapter<NoteObject>({
  selectId: item => item.id
});

// tslint:disable-next-line:no-any
const isOk = (status: number): boolean => status === 200 || status === 201 || status === 204;

// tslint:disable-next-line:no-any
const rejectFromResponse = (api: any, response: any) => {
  if (response.payload?.response?.data?.errors) {
    return api.rejectWithValue({ errors: response.payload.response.data.errors });
  }

  return api.rejectWithValue({ serverError: `${response.payload?.response?.status}` });
};

const jobRelationship = (jobId: string | number) => ({
  job: { data: { type: JOB_RESOURCE_TYPE, id: String(jobId) } }
});

// Get every note the requesting pro wrote on a job (newest first, per the viewset ordering).
export const getNotes = createAsyncThunk<Array<NoteObject>, string | number, RejectValue>(
  'notes/v3/get',
  async (jobId, api) => {
    try {
      const response = await makeRequest(
        `${NOTE_ENDPOINT}/`,
        {
          method: 'GET',
          additionalGetParams: {
            'filter[job]': String(jobId),
            'page[size]': PAGE_SIZE
          }
        }
      )(api);

      if (!isOk(response.status)) {
        return rejectFromResponse(api, response);
      }

      return response.data.data;
    } catch (error) {
      return api.rejectWithValue({ error: error.message });
    }
  }
);

export const addNote = createAsyncThunk<NoteObject, { jobId: string | number, body: string }, RejectValue>(
  'notes/v3/add',
  async ({ jobId, body }, api) => {
    try {
      const response = await makeRequest(
        `${NOTE_ENDPOINT}/`,
        {
          method: 'POST',
          body: {
            data: {
              type: NOTE_RESOURCE_TYPE,
              attributes: { body },
              relationships: jobRelationship(jobId)
            }
          }
        }
      )(api);

      if (!isOk(response.status)) {
        return rejectFromResponse(api, response);
      }

      return response.data.data;
    } catch (error) {
      return api.rejectWithValue({ error: error.message });
    }
  }
);

export const updateNote = createAsyncThunk<NoteObject, { id: string, jobId: string | number, body: string }, RejectValue>(
  'notes/v3/update',
  async ({ id, jobId, body }, api) => {
    try {
      const response = await makeRequest(
        `${NOTE_ENDPOINT}/${id}`,
        {
          method: 'PATCH',
          body: {
            data: {
              type: NOTE_RESOURCE_TYPE,
              id: String(id),
              attributes: { body },
              relationships: jobRelationship(jobId)
            }
          }
        }
      )(api);

      if (!isOk(response.status)) {
        return rejectFromResponse(api, response);
      }

      return response.data.data;
    } catch (error) {
      return api.rejectWithValue({ error: error.message });
    }
  }
);

export const deleteNote = createAsyncThunk<string, { id: string }, RejectValue>(
  'notes/v3/delete',
  async ({ id }, api) => {
    try {
      const response = await makeRequest(
        `${NOTE_ENDPOINT}/${id}`,
        { method: 'DELETE' }
      )(api);

      if (!isOk(response.status)) {
        return rejectFromResponse(api, response);
      }

      return String(id);
    } catch (error) {
      return api.rejectWithValue({ error: error.message });
    }
  }
);

export const noteSlice = createSlice({
  name: 'note',
  initialState: noteAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<NoteObject>),
  reducers: {
    // Notes sideloaded on the job list (bid/outerJob `latestNote` relationship).
    noteAdded: (state, action) => {
      noteAdapter.setMany(state, action.payload);
    }
  },
  extraReducers: builder => {
    builder.addCase(getNotes.pending, state => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getNotes.fulfilled, (state, action) => {
      noteAdapter.upsertMany(state, action.payload);
      state.loading = false;
    });
    builder.addCase(getNotes.rejected, state => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(addNote.fulfilled, (state, action) => {
      noteAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(addNote.rejected, state => {
      state.error = true;
    });
    builder.addCase(updateNote.fulfilled, (state, action) => {
      noteAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(updateNote.rejected, state => {
      state.error = true;
    });
    builder.addCase(deleteNote.fulfilled, (state, action) => {
      noteAdapter.removeOne(state, action.payload);
    });
    builder.addCase(deleteNote.rejected, state => {
      state.error = true;
    });
  }
});

export const { noteAdded } = noteSlice.actions;
export const noteSelectors = noteAdapter.getSelectors((state: RootState) => state.note);

// Notes belonging to a single job, newest first — the note's job lives on its relationship.
export const selectNotesByJob = createSelector(
  [noteSelectors.selectAll, (state: RootState, jobId: string | number) => String(jobId)],
  (notes, jobId) => notes
    .filter(note => note.relationships?.job?.data?.id === jobId)
    .sort((a, b) => {
      const createdA = String(a.attributes?.created ?? '');
      const createdB = String(b.attributes?.created ?? '');

      return createdB.localeCompare(createdA);
    })
);
